import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { ANY_SPORT_MY_CLUB_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';

import { PublicWatchlists } from '@sorare/marketplace/src/components/watchlists/PublicWatchlists';
import { PublicWatchlistsTitle } from '@sorare/marketplace/src/components/watchlists/PublicWatchlistsTitle';

import type {
  UserWatchlistsQuery,
  UserWatchlistsQueryVariables,
} from './__generated__/index.graphql.ts';

type Props = {
  slug: string;
};

const USER_WATCHLISTS_QUERY = gql`
  query UserWatchlistsQuery($slug: String!, $sport: Sport!, $cursor: String) {
    user(slug: $slug) {
      slug
      publicWatchlists(sport: $sport, first: 20, after: $cursor) {
        nodes {
          slug
          ...PublicWatchlists_watchlist
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
  ${PublicWatchlists.fragments.watchlist}
` as TypedDocumentNode<UserWatchlistsQuery, UserWatchlistsQueryVariables>;

export const UserWatchlists = ({ slug }: Props) => {
  const { sport, generateSportContextPath } = useSportContext();
  const { data, loadMore, loading } = usePaginatedQuery(USER_WATCHLISTS_QUERY, {
    variables: { slug, sport },
    connection: 'WatchlistConnection',
    skip: !slug,
  });

  const { publicWatchlists } = data?.user || {};

  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore(false, {
        cursor: publicWatchlists?.pageInfo.endCursor,
      });
    },
    publicWatchlists?.pageInfo.hasNextPage || false,
    loading
  );

  return (
    <PublicWatchlists
      publicWatchlists={publicWatchlists?.nodes || []}
      redirectTo={generateSportContextPath(ANY_SPORT_MY_CLUB_SLUG, {
        params: { slug },
      })}
      redirectIfEmpty
      infiniteScrollNode={<InfiniteScrollLoader />}
      hideOwner
      loading={loading}
      title={
        <PublicWatchlistsTitle count={publicWatchlists?.nodes?.length || 0} />
      }
    />
  );
};
